import clsx from 'clsx';
import TreeMapHelp from '@/treemap/TreeMapHelp.atom';
import type { TreemapItem } from '@/treemap/TreemapChart.atom';
import { TreemapChart } from '@/treemap/TreemapChart.atom';
import { Alert, Icon } from '@seeqdev/qomponents';
import { FakeLink } from '@/core/FakeLink';
import type { TreemapBreadcrumbs, TreemapParent } from '@/treemap/treemap.store';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { provideVisualizationData } from '@/annotation/ckEditorPlugins/components/content.utilities';
import { Visualization } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import type { SerializedRange } from '@/datetime/dateTime.utilities';
import { deserializeRange } from '@/datetime/dateTime.utilities';
import type { Timezone } from '@/datetime/timezone.service';
import { DisplayRangeSelector } from '@/trend/DisplayRangeSelector.molecule';
import { useTranslation } from 'react-i18next';
import { TreemapConfigurationModal } from '@/treemap/TreemapConfigurationModal.atom';
import type { SelectOption } from '@/formbuilder/SelectFormComponent';
import type { ColumnOrRowWithDefinitions } from '@/tableBuilder/tableBuilder.types';

export interface TreemapPropsOnlyProps {
  helpKey?: string;
  breadcrumbs: TreemapBreadcrumbs;
  parent: TreemapParent;
  tree: TreemapItem[];
  isContent: boolean;
  /** Note: this needs to be serialized in order to render properly in Organizer as interactive content */
  displayRange: SerializedRange;
  timezone: Timezone;
  rangeEditingEnabled: boolean;
  onClickNode: (asset: { id: string; name?: string; assetPathId?: string } | undefined, isLeafNode: boolean) => void;
  afterChartUpdate?: () => void;
  isTableView?: boolean;
  showModal?: boolean;
  handleModalState: (open: boolean) => void;
  simpleColumns?: ColumnOrRowWithDefinitions[];
  selectOptionsSignal?: SelectOption<any>[];
  proportionBySignal?: string;
  selectOptionsColumn?: SelectOption<any>[];
  proportionByColumn?: string;
  isUniformSizing: boolean;
  treemapSquareScalingInverse: boolean;
  saveTreemapConfiguration: (
    selectedSignal: string | undefined,
    selectedColumn: string | undefined,
    treemapSquareScalingInverse: boolean,
    isUniformSizing: boolean,
  ) => void;
}

export const TreemapPropsOnly: React.FunctionComponent<TreemapPropsOnlyProps> = (props) => {
  const {
    helpKey,
    breadcrumbs,
    parent,
    tree,
    isContent,
    displayRange,
    timezone,
    rangeEditingEnabled,
    onClickNode,
    afterChartUpdate = () => {},
    isTableView,
    showModal = false,
    handleModalState = () => {},
    selectOptionsSignal = [],
    proportionBySignal,
    selectOptionsColumn = [],
    proportionByColumn,
    isUniformSizing,
    treemapSquareScalingInverse,
    saveTreemapConfiguration,
  } = props;
  const { t } = useTranslation();

  if (headlessRenderMode()) {
    provideVisualizationData({
      ...props,
      visualization: Visualization.TREEMAP,
    });
  }

  const handleModalSave = (
    selectedSignal: any,
    selectedColumn: any,
    treemapSquareScalingInverse: boolean,
    isUniformSizing: boolean,
  ) => {
    saveTreemapConfiguration(selectedSignal, selectedColumn, treemapSquareScalingInverse, isUniformSizing);
    handleModalState(false);
  };

  const renderBreadcrumbs = () => (
    <ul
      className={clsx('unstyled-list searchBreadcrumbs flexColumnContainer flexWrap flexNoGrowNoShrink mb0 ml12', {
        mt5: isContent,
      })}
      data-testid="treemapBreadcrumbs">
      {breadcrumbs.map((breadcrumb) => (
        <li key={breadcrumb.id} className={clsx({ active: breadcrumb.id === parent.id || isContent })}>
          {breadcrumb.id !== parent.id && !isContent ? (
            <FakeLink onClick={() => onClickNode(breadcrumb, false)}>{breadcrumb.name}</FakeLink>
          ) : (
            breadcrumb.name
          )}
        </li>
      ))}
    </ul>
  );

  const renderTreemapConfig = () => (
    <div className="flexColumnContainer flexBaseline">
      {isUniformSizing || !proportionByColumn || !proportionBySignal ? (
        <FakeLink onClick={() => handleModalState(true)}>{t('TREEMAP.PROPORTION_BY_UNIFORM')}</FakeLink>
      ) : (
        <FakeLink onClick={() => handleModalState(true)}>
          {t('TREEMAP.PROPORTION_BY', {
            signal: proportionBySignal,
            column: t(`${proportionByColumn.toUpperCase()}.LONG`),
          })}
        </FakeLink>
      )}
      <Icon
        icon="fc fc-gear"
        testId="treemap-config-gear"
        extraClassNames="pl5"
        onClick={() => handleModalState(true)}
      />
    </div>
  );

  return (
    <>
      {!helpKey && (
        <div className="flexColumnContainer flexSpaceBetween">
          {renderBreadcrumbs()}
          {isTableView && renderTreemapConfig()}
        </div>
      )}

      {helpKey && (
        <div className="flexRowContainer flexCenter flexFill" data-testid="treemapHelp">
          <Alert variant="theme" dismissible={false} extraClassNames="fs16 p25 flexSelfCenter max-width-400">
            <TreeMapHelp type={helpKey} />
          </Alert>
        </div>
      )}

      <TreemapConfigurationModal
        show={showModal}
        onClose={() => handleModalState(false)}
        onSubmit={handleModalSave}
        selectOptionsSignal={selectOptionsSignal}
        proportionBySignal={proportionBySignal}
        selectOptionsColumn={selectOptionsColumn}
        proportionByColumn={proportionByColumn}
        isUniformSizing={isUniformSizing || !proportionBySignal || !proportionByColumn}
        treemapSquareScalingInverse={treemapSquareScalingInverse}
      />

      {!helpKey && (
        <div className="flexFill flexColumnContainer height-maximum mr0 mb0 ml0" data-testid="treemapChart">
          <TreemapChart
            tree={tree}
            isContent={isContent}
            afterChartUpdate={afterChartUpdate}
            onClickNode={onClickNode}
            isTableBuilderTreemap={isTableView}
          />
        </div>
      )}
      {!isTableView && (
        <DisplayRangeSelector
          timezone={timezone}
          displayRange={deserializeRange(displayRange)}
          rangeEditingEnabled={rangeEditingEnabled}
          investigateRangeCopyDisabled={true}
          isInTopic={isContent}
          autoUpdateDisabled={isContent}
        />
      )}
    </>
  );
};
